
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface LinkTestResult {
  action_link: string;
  redirectTo: string;
  email: string;
  timestamp: string;
}

// Helper function to validate absolute HTTPS URLs
const isAbsoluteHttps = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

export const AdminResetLinkTester = () => {
  const [email, setEmail] = useState('');
  const [redirectTo, setRedirectTo] = useState('https://iflsh.com/reset-password');
  const [result, setResult] = useState<LinkTestResult | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const generateLink = async () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isAbsoluteHttps(redirectTo.trim())) {
      toast.error('Invalid redirect URL - Use a full https:// URL like https://iflsh.com/reset-password');
      setError('Redirect URL must be a full https:// URL');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Please sign in as an admin first');
        setLoading(false);
        return;
      }

      console.log('🔧 Calling recover-link-tester function...');
      
      const response = await fetch(
        `https://xdcjjtsakwthfuywzyjv.supabase.co/functions/v1/recover-link-tester`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ email: email.trim(), redirectTo: redirectTo.trim() }),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('❌ Function error:', data);
        setError(data.error || `HTTP ${response.status}: ${response.statusText}`);
        return;
      }

      console.log('✅ Function success:', data);
      setResult(data);
      
      toast.success('Recovery link generated successfully!');
    } catch (err) {
      console.error('❌ Request error:', err);
      setError(`Network error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const analyzeLink = (actionLink: string) => {
    try {
      const url = new URL(actionLink);
      const redirectParam = url.searchParams.get('redirect_to');
      const hasCorrectRedirect = redirectParam === redirectTo;
      
      return {
        hasRedirectParam: !!redirectParam,
        redirectParam,
        hasCorrectRedirect,
        isSupabaseUrl: url.hostname.includes('supabase.co'),
        hasTokenHash: url.searchParams.has('token_hash'),
        hasType: url.searchParams.get('type') === 'recovery',
      };
    } catch {
      return null;
    }
  };

  const linkAnalysis = result ? analyzeLink(result.action_link) : null;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🔧 Reset Link Tester
            <span className="text-sm font-normal text-muted-foreground">(Admin Only)</span>
          </CardTitle>
          <CardDescription>
            Generate and analyze the exact recovery link that Supabase would send via email.
            This helps debug "requested path is invalid" errors.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">User Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="redirectTo">Redirect URL</Label>
              <Input
                id="redirectTo"
                placeholder="https://iflsh.com/reset-password"
                value={redirectTo}
                onChange={(e) => setRedirectTo(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <Button 
            onClick={generateLink} 
            disabled={loading || !email.trim()}
            className="w-full md:w-auto"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Generate Recovery Link
          </Button>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Recovery link generated successfully at {new Date(result.timestamp).toLocaleString()}
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Generated Action Link:</Label>
                  <div className="mt-1 p-3 bg-muted rounded-md break-all text-sm font-mono">
                    {result.action_link}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.open(result.action_link, '_blank')}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Test Link
                  </Button>
                </div>

                {linkAnalysis && (
                  <div>
                    <Label className="text-sm font-semibold">Link Analysis:</Label>
                    <div className="mt-1 space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className={`flex items-center gap-2 ${linkAnalysis.isSupabaseUrl ? 'text-green-600' : 'text-red-600'}`}>
                          {linkAnalysis.isSupabaseUrl ? '✅' : '❌'} Valid Supabase URL
                        </div>
                        <div className={`flex items-center gap-2 ${linkAnalysis.hasTokenHash ? 'text-green-600' : 'text-red-600'}`}>
                          {linkAnalysis.hasTokenHash ? '✅' : '❌'} Has token_hash
                        </div>
                        <div className={`flex items-center gap-2 ${linkAnalysis.hasType ? 'text-green-600' : 'text-red-600'}`}>
                          {linkAnalysis.hasType ? '✅' : '❌'} Type = recovery
                        </div>
                        <div className={`flex items-center gap-2 ${linkAnalysis.hasRedirectParam ? 'text-green-600' : 'text-red-600'}`}>
                          {linkAnalysis.hasRedirectParam ? '✅' : '❌'} Has redirect_to param
                        </div>
                      </div>
                      
                      {linkAnalysis.redirectParam && (
                        <div className="mt-3">
                          <Label className="text-xs text-muted-foreground">Redirect Parameter:</Label>
                          <div className="p-2 bg-muted rounded text-xs font-mono break-all">
                            {decodeURIComponent(linkAnalysis.redirectParam)}
                          </div>
                          <div className={`text-xs mt-1 ${linkAnalysis.hasCorrectRedirect ? 'text-green-600' : 'text-orange-600'}`}>
                            {linkAnalysis.hasCorrectRedirect ? '✅ Matches expected redirect URL' : '⚠️ Different from expected redirect URL'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>What to look for:</strong> The action_link should contain 
              <code className="mx-1 px-1 bg-muted rounded">redirect_to=https%3A%2F%2Fiflsh.com%2Freset-password</code>
              If this parameter is missing or incorrect, update your Supabase Auth URL Configuration.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminResetLinkTester;
